import * as PropTypes from 'prop-types';
import React, {ChangeEvent, FocusEvent, Ref} from 'react';
import _ from 'underscore';

import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {ElementKeyboardEvent} from 'util/Types';

import {
  DEFAULT_PROPS,
  INPUT,
  InputProps,
  LABEL_STYLES,
  SIZES,
  TEXT_INPUT_TYPES,
  TextInputType,
} from './InputConstants';
import InputWrapper from './InputWrapper';
import TextInput from './TextInput';

export interface TextFieldProps extends InputProps {
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  autoFocus?: boolean;
  autoComplete?: string;
  defaultValue?: string;
  disabled?: boolean;
  hasError?: boolean;
  name?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  placeholder?: string;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  type?: TextInputType;
  value?: string;
  maxLength?: number;
  dataTestAttrs?: DataTestAttrs;
  textInputRef?: Ref<TextInput>;
  helpInteraction?: FloatingInteraction.CLICK | FloatingInteraction.HOVER;
}

/**
 * Component for input of string fields, providing labels, icons, and validation.
 * Label styles are re-exported on this class.
 */
export default class TextField extends React.Component<TextFieldProps> {
  static SIZES = SIZES;
  static LABEL_STYLES = LABEL_STYLES;
  static TYPES = TEXT_INPUT_TYPES;

  id = _.uniqueId('fieldInput-');

  static propTypes = {
    ...INPUT,
    autoFocus: PropTypes.bool.isRequired,
    autoComplete: PropTypes.string,
    defaultValue: PropTypes.string,
    disabled: PropTypes.bool,
    hasError: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    onPaste: PropTypes.func,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    type: PropTypes.oneOf(Object.values(TEXT_INPUT_TYPES)).isRequired,
    value: PropTypes.string,
    maxLength: PropTypes.number,
    dataTestAttrs: DataTestAttrsType,
    textInputRef: PropTypes.shape({current: PropTypes.instanceOf(TextInput)}),
    helpInteraction: PropTypes.oneOf(Object.values(FloatingInteraction)),
  };

  static defaultProps = {
    ...DEFAULT_PROPS,
    autoFocus: false,
    type: TextInputType.TEXT,
  };

  render(): JSX.Element {
    return (
      <InputWrapper
        button={this.props.button}
        className={this.props.className}
        description={this.props.description}
        id={this.id}
        isRequired={this.props.isRequired}
        label={this.props.label}
        labelStyle={this.props.labelStyle}
        error={this.props.error}
        size={this.props.size}
        help={this.props.help}
        helpInteraction={this.props.helpInteraction}
      >
        <TextInput
          id={this.id}
          autoFocus={this.props.autoFocus}
          className={this.props.inputClassName}
          defaultValue={this.props.defaultValue}
          disabled={this.props.disabled}
          hasButton={Boolean(this.props.button)}
          hasError={this.props.hasError}
          name={this.props.name}
          size={this.props.size}
          value={this.props.value}
          onChange={this.props.onChange}
          onBlur={this.props.onBlur}
          onKeyDown={this.props.onKeyDown}
          onPaste={this.props.onPaste}
          placeholder={this.props.placeholder}
          type={this.props.type}
          autoComplete={this.props.autoComplete}
          readOnly={this.props.readOnly}
          maxLength={this.props.maxLength}
          dataTestAttrs={this.props.dataTestAttrs}
          ref={this.props.textInputRef}
        />
      </InputWrapper>
    );
  }
}
